import uuid from 'node-uuid';
import { extendObservable, action, computed, autorun } from 'mobx';
import includes from 'lodash/includes';
import some from 'lodash/some';
import method from 'lodash/method';
import axios, { setAxiosRootStoreAlertHandler } from '../../../libs/axios_csrf';

import Card from './Card';
import { LANE_NAMES, NOTES_TO_LOAD, PRIORITIES_TO_INTEGER } from '../constants';
import { calculateNewPosition } from '../../common_components/functions';

export default class LaneStore {
  constructor(rootStore, { id, name, cards, cardsCount, statistics }) {
    this.rootStore = rootStore;
    this.id = id;
    this.name = name;

    let sortedCards = cards;
    if (name !== LANE_NAMES.DONE) {
      sortedCards = this.sortNotes(cards);
    }

    extendObservable(this, {
      notesCount: cardsCount,
      notes: sortedCards.map(c => {
        return new Card(this, c);
      }),
      hasMoreNotes: true,
      statistics,
      loading: false
    });

    setAxiosRootStoreAlertHandler(this.rootStore);
  }

  @action
  addNote(note) {
    this.notesCount++;

    const newNote = new Card(this, note, !!note.id);
    this.notes.unshift(newNote);

    this._handleNewNoteFilter(newNote);

    // ------  AJAX CALL ----
    if (!note.id)
      // Is note already persisted?
      axios
        .post('/cards.json', {
          card: {
            list_id: this.id,
            name: newNote.name,
            description: '',
            position: newNote.position
          }
        })
        .then(
          action(response => {
            newNote.id = response.data.id;
            newNote.todoList = response.data.todoList;
            newNote.noteAlarm = response.data.cardAlarm;
            newNote.persisted = true;
            this.rootStore.triggerChartUpdates();
          })
        )
        .catch(
          action(error => {
            console.error(error);
            this.notes.remove(newNote);
          })
        );

    //------------------------------------------------
  }

  @action
  addLane({ name }) {
    this.rootStore.lanes.push({ id: uuid.v4(), name, notes: [] });
  }

  @action
  editLane(lane, name) {
    lane.name = name;
  }

  @action
  deleteLane(lane) {
    const { lanes } = this.rootStore;
    const laneIndex = lanes.indexOf(lane);

    if (laneIndex < 0) {
      return;
    }

    lane.notes.forEach(note => Card.deleteNote(note));
    lanes.splice(laneIndex, 1);
  }

  // Moves the given note to a target lane
  @action
  moveNoteFromDropdown(note) {
    if (!note.persisted) {
      return;
    }

    const noteIndex = note.laneStore.notes.indexOf(note);
    note.laneStore.notes.splice(noteIndex, 1);
    note.laneStore._decrementNotesCounter();

    this.notes.splice(0, 0, note);
    note.laneStore = this;
    this.notesCount++;

    note.position = this.notes[0] ? this.notes[0].position - 0.1 : 0;
    note.updateLastUser();
    this.loading = true;

    // ------  AJAX CALL ----------------------------
    axios
      .patch(`/cards/${note.id}.json`, {
        card: {
          list_id: this.id,
          position: note.position
        }
      })
      .then(
        action(response => {
          // console.log("response: ", response);
          this.loading = false;
          note.priorBoard = response.data.priorBoard;

          this.rootStore.triggerChartUpdates();
          this.rootStore.generateAlert({
            type: 'success',
            headline: 'card_moved_success_alert'
          });
        })
      )
      .catch(
        action(error => {
          this.loading = false;
          console.log(error);
        })
      );
    //------------------------------------------------
  }

  @action
  updateNotePosition(note, index, laneChanged, laneName = '') {
    if (!note.persisted) {
      return;
    }

    // let position = calculateNewPosition(this.notes, index);
    note.position = calculateNewPosition(this.notes, index);

    // note.position = position;
    note.updateLastUser();

    // ------  AJAX CALL ----------------------------
    this.loading = true;
    axios
      .patch(`/cards/${note.id}.json`, {
        card: {
          list_id: laneChanged ? this.id : undefined,
          position: note.position
        },
        foreign: this.rootStore.isInclusiveBoard, // if the card is in a foreign lane (Selected all/member in TaskPanel)
        lane: laneName
      })
      .then(
        action(response => {
          // console.log("response: ", response);
          this.loading = false;
          laneChanged && this.rootStore.triggerChartUpdates();

          if (note.assignee == undefined)
            note.editAssignee(this.rootStore.user);

          note.priorBoard = response.data.priorBoard;
          /* else if (!note.teamAssignees.includes(this.rootStore.user)) {
            note.addNewMemberToTeamAssignees(this.rootStore.user);
          } */

          //  console.log('Assigned guy: ', this.rootStore.user);
        })
      )
      .catch(
        action(error => {
          this.loading = false;
          console.log(error);
        })
      );
    //------------------------------------------------
  }

  @action
  loadMoreNotes(page) {
    // Stop scroll loading and wait for the ajax response.
    this.hasMoreNotes = false;

    // ------------------  AJAX CALL ---------------------------------

    axios
      .get(`/igtasks/${this.rootStore.boardId}/cards.json`, {
        params: {
          board_list_id: this.id,
          page
        }
      })
      .then(
        action(response => {
          this.notes = this.notes.concat(
            response.data.map(c => new Card(this, c))
          );

          // if response data has full NOTES_TO_LOAD's length (i.e: 10 notes of 10), then set hasMoreNotes to true
          if (response.data.length >= NOTES_TO_LOAD) {
            this.hasMoreNotes = true;
          }
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
    // ----------------------------------------------------------------
  }

  load() {
    /*
        const rawLanes = storage.get('LaneStore') || [];

        return rawLanes.map(lane => ({
            ...lane,
            notes: lane.notes.map(id => Card.find(id))
        }));
        */
  }

  persist() {
    /*
        // Whenever the Json representation of the lanes changes, store it.
        autorun(() => {
            storage.set('LaneStore', this.toJson);
        });
        */
  }

  sortNotes(notes) {
    return notes.sort((a, b) => {
      if (a.priority === b.priority) {
        return b.position - a.position;
      }
      return (
        PRIORITIES_TO_INTEGER[b.priority] - PRIORITIES_TO_INTEGER[a.priority]
      );
    });
  }

  @action
  _decrementNotesCounter() {
    this.notesCount--;
    if (this.notesCount < 0) this.notesCount = 0;
  }

  @computed get filteredNotes() {
    try {
      const query = this.rootStore.filterQuery.toLowerCase();

      // console.log('Notes values:', this.notes)

      if (query.trim()) {
        return this.notes.filter(note => {
          return (
            note.isNew ||
            includes(note.name.toLowerCase(), query) ||
            includes(note.description.toLowerCase(), query) ||
            includes(note.creator.name.toLowerCase(), query) ||
            some(note.labelsNames, method('includes', query)) ||
            some(note.commentsBodies, method('includes', query)) ||
            some(note.todosDescriptions, method('includes', query))
          );
        });
      }
      return this.notes;
    } catch (e) {
      console.log(e);
      return this.notes;
    }
  }

  findNoteById(id) {
    return this.notes.find(note => note.id === id);
  }

  // set the "isNew" status to the note for limited time, if it was created while there is a filterQuery.
  _handleNewNoteFilter(note) {
    if (this.rootStore.filterQuery.trim()) {
      note.isNew = true;

      this._noteIsNewTimeOut(note).then(newNote => {
        newNote.isNew = false;
      });
    }
  }

  // simple timeout promise that receive and return the same note object.
  _noteIsNewTimeOut = note => {
    return new Promise((resolve, reject) => {
      if (note) {
        setTimeout(function() {
          resolve(note);
        }, 5000);
      }
    });
  };
}
